/**
 * Supported languages are shared across apps. Namespaces (resources) differ per
 * app and are composed there; the language set + detection live here.
 */

// UI is Italian-only for now (see apps/admin AGENTS.md/CLAUDE.md). English is
// kept in the type only because shared utils reference it; the app never
// selects it. Do NOT re-enable other languages without also restoring the
// i18n parity check and translating every namespace.
const DEFAULT_LANGUAGE_CODE = 'it' as const

export type AppLanguageCode = 'en' | 'it'

export interface AppLanguage {
	label: string
	code: AppLanguageCode
}

export const DEFAULT_LANGUAGE = {
	label: 'Italiano (IT)',
	code: DEFAULT_LANGUAGE_CODE
} as const satisfies AppLanguage

export const APP_LANGUAGES = [
	DEFAULT_LANGUAGE
] as const satisfies readonly AppLanguage[]

export function getInitialAppLanguage(): AppLanguageCode {
	if (typeof window === 'undefined') {
		return DEFAULT_LANGUAGE.code
	}

	const documentLanguage = document.documentElement.lang || undefined

	return resolveAppLanguageCode(
		documentLanguage ??
			getStoredLanguage() ??
			getCookieValue(document.cookie, 'i18nextLng') ??
			window.navigator.language
	)
}

export function detectAppLanguageFromRequest(
	request: Request
): AppLanguageCode {
	return resolveAppLanguageCode(
		getCookieValue(request.headers.get('cookie'), 'i18nextLng') ??
			request.headers.get('accept-language')
	)
}

function resolveAppLanguageCode(
	value: string | null | undefined
): AppLanguageCode {
	if (!value) {
		return DEFAULT_LANGUAGE.code
	}

	const candidates = value
		.split(',')
		.map((candidate) => candidate.split(';')[0]?.trim().toLowerCase())
		.filter(Boolean)

	for (const candidate of candidates) {
		const language = APP_LANGUAGES.find((appLanguage) => {
			const normalizedCode = appLanguage.code.toLowerCase()
			return (
				candidate === normalizedCode ||
				candidate?.startsWith(`${normalizedCode}-`)
			)
		})

		if (language) {
			return language.code
		}
	}

	return DEFAULT_LANGUAGE.code
}

function getCookieValue(cookieHeader: string | null, key: string) {
	if (!cookieHeader) {
		return
	}

	const cookies = cookieHeader.split(';')
	for (const cookie of cookies) {
		const [cookieKey, ...valueParts] = cookie.trim().split('=')
		if (cookieKey === key) {
			try {
				return decodeURIComponent(valueParts.join('='))
			} catch {
				return
			}
		}
	}
}

function getStoredLanguage() {
	try {
		return window.localStorage.getItem('i18nextLng')
	} catch {
		return
	}
}
